// utils/api.js - Gestionnaire d'appels API avec authentification
import { handleApiError, checkConnection } from './errorHandler.jsx';
import { getApiUrl, isProduction } from './env.js';
// Les toasts sont maintenant gérés manuellement dans les composants avec toast.success/error

// Obtenir l'URL de l'API selon l'environnement
const API_BASE_URL = getApiUrl();

// Log pour débogage
if (typeof window !== 'undefined') {
  console.log('🔧 Configuration API:');
  console.log('  - Production détectée:', isProduction());
  console.log('  - API_BASE_URL:', API_BASE_URL);
  console.log('  - hostname:', window.location.hostname);
  if (isProduction()) {
    console.log('  ✅ URL API forcée à https://app-secura.com (PRODUCTION)');
  } else {
    console.log('  🔧 Mode développement détecté');
  }
}

// Fonction pour obtenir le token depuis les cookies
let getTokenFromContext = () => {
  try {
    // Essayer d'abord localStorage (pour compatibilité)
    const localToken = localStorage.getItem('token');
    console.log('Token localStorage:', localToken ? 'Trouvé' : 'Non trouvé');
    if (localToken) return localToken;
    
    // Sinon, essayer de récupérer depuis les cookies
    const cookies = document.cookie.split(';');
    console.log('Cookies disponibles:', document.cookie);
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'token') {
        console.log('Token cookie trouvé:', value ? 'Oui' : 'Non');
        return value;
      }
    }
    console.log('Aucun token trouvé');
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return null;
  }
};

// Fonction pour obtenir l'entreprise_id depuis le contexte (sera remplacée par le contexte)
let getEntrepriseIdFromContext = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).entreprise_id : null;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération de l\'entreprise_id:', error);
    return null;
  }
};

// Fonction pour définir la fonction de récupération du token depuis le contexte
export const setTokenGetter = (tokenGetter) => {
  getTokenFromContext = tokenGetter;
};

// Fonction pour définir la fonction de récupération de l'entreprise_id depuis le contexte
export const setEntrepriseIdGetter = (entrepriseIdGetter) => {
  getEntrepriseIdFromContext = entrepriseIdGetter;
};

const getToken = () => {
  return getTokenFromContext();
};

const getEntrepriseId = () => {
  return getEntrepriseIdFromContext();
};

export const isAuthenticated = () => {
  const token = getToken();
  const user = localStorage.getItem('user');
  return token && user;
};

export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const apiCall = async (endpoint, options = {}) => {
  // Vérifier la connexion avant de faire la requête
  if (!checkConnection()) {
    const error = new Error('Pas de connexion Internet');
    handleApiError(error);
    throw error;
  }

  const entrepriseId = getEntrepriseId();

  // Routes accessibles sans authentification (activation d'invitation, etc.)
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/reset-password',
    '/auth/request-reset-password',
    '/auth/validate-company-token',
    '/auth/activate-company',
    '/api/admin-rh-plus/activate',
    '/api/admin-rh-plus/invitation'
  ];

  const isPublicEndpoint = publicEndpoints.some(route => endpoint.startsWith(route));
  const token = getToken();

  console.log('🔍 API Call - Endpoint:', endpoint);
  console.log('🔍 API Call - Endpoint public:', isPublicEndpoint);
  console.log('🔍 API Call - Token présent:', !!token);
  console.log('🔍 API Call - Entreprise ID:', entrepriseId);

  if (!token && !isPublicEndpoint) {
    const error = new Error('Token d\'authentification manquant');
    // Si silent est true, ne pas afficher l'erreur ni la lancer
    if (!options.silent) {
      handleApiError(error);
    }
    throw error;
  }

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers
  };

  if (!isPublicEndpoint && token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const defaultOptions = {
    headers: defaultHeaders,
    credentials: 'include', // Inclure les cookies
    ...options,
    headers: defaultHeaders
  };

  if (options.headers) {
    defaultOptions.headers = { ...defaultOptions.headers, ...options.headers };
  }
  

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, defaultOptions);
  } catch (error) {
    // Gérer les erreurs réseau
    handleApiError(error);
    throw error;
  }

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    handleApiError({ response: { status: 401 } }, 401);
    window.location.href = '/login';
    throw new Error('Session expirée');
  }

  if (!response.ok) {
    // Créer un objet error avec les informations de la réponse
    let errorData = null;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else if (contentType && contentType.includes('text/html')) {
        // Si on reçoit du HTML, c'est probablement une page 404
        const errorText = await response.text();
        errorData = { 
          message: `La route API n'existe pas ou n'est pas accessible. Status: ${response.status}` 
        };
      } else {
        const errorText = await response.text();
        errorData = { message: errorText };
      }
    } catch (parseError) {
      errorData = { message: `Erreur ${response.status}` };
    }

    const error = {
      response: {
        status: response.status,
        data: errorData
      },
      status: response.status,
      data: errorData,
      message: errorData?.message || `Erreur API: ${response.status}`
    };

    // Toujours appeler handleApiError, il détectera automatiquement les erreurs de permission
    // et ne les affichera pas. Si l'option silent est activée, handleApiError ne sera pas appelé
    // mais cela ne devrait pas être nécessaire car les erreurs de permission sont déjà gérées.
    if (!options.silent) {
      handleApiError(error, response.status);
    }

    throw error;
  }

  return response;
};

// Fonction pour ajouter l'entreprise_id aux paramètres d'une requête
const addEntrepriseIdToParams = (endpoint) => {
  const entrepriseId = getEntrepriseId();
  if (!entrepriseId) return endpoint;
  
  // Routes qui ne doivent PAS avoir entreprise_id (l'ID vient du token JWT)
  const routesWithoutEntrepriseId = [
    '/auth/me',
    '/auth/login', 
    '/auth/logout', 
    '/auth/register', 
    '/auth/reset-password',
    '/auth/is-auth',
    '/entreprise/settings',
    '/api/entreprise/settings',
    '/entreprise/subscription-status',
    '/entreprise/appearance',
    '/entreprise/schedule-settings',
    '/api/entreprise/schedule-settings'
  ];
  
  if (routesWithoutEntrepriseId.some(route => endpoint.startsWith(route))) {
    return endpoint;
  }
  
  // Vérifier si entreprise_id est déjà présent dans l'URL pour éviter la duplication
  if (endpoint.includes('entreprise_id=')) {
    // Remplacer la valeur existante si elle est différente
    const urlPattern = /([?&])entreprise_id=[^&]*/g;
    if (urlPattern.test(endpoint)) {
      return endpoint.replace(urlPattern, `$1entreprise_id=${entrepriseId}`);
    }
    return endpoint;
  }
  
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}entreprise_id=${entrepriseId}`;
};

export const apiGet = async (endpoint, options = {}) => {
  try {
    const endpointWithEntreprise = addEntrepriseIdToParams(endpoint);
    const response = await apiCall(endpointWithEntreprise, { method: 'GET', ...options });
    
    // Vérifier le Content-Type avant de parser en JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      
      // Si on reçoit du HTML, c'est probablement que la route n'existe pas et qu'on reçoit la page 404 HTML
      if (contentType && contentType.includes('text/html')) {
        console.warn('⚠️ Réponse HTML reçue au lieu de JSON - La route API pourrait ne pas exister:', endpoint);
        const error = {
          message: `La route API "${endpoint}" n'existe pas ou n'est pas accessible.`,
          response: { status: 404 },
          status: 404
        };
        if (!options.silent) {
          handleApiError(error, 404);
        }
        throw error;
      }
      
      console.error('Réponse non-JSON reçue:', text.substring(0, 200));
      const error = new Error(`Réponse non-JSON reçue. Status: ${response.status}, Content-Type: ${contentType}`);
      if (!options.silent) {
        handleApiError(error, response.status);
      }
      throw error;
    }
    
    return response.json();
  } catch (error) {
    // Si l'erreur vient d'apiCall, elle est déjà gérée
    // Si c'est une nouvelle erreur (comme le HTML), on doit la gérer ici
    if (!options.silent && !error.response && error.message && !error.message.includes('apiCall')) {
      handleApiError(error);
    }
    throw error;
  }
};

export const apiPost = async (endpoint, data, options = {}) => {
  // Ajouter l'entreprise_id aux données si pas déjà présent
  const entrepriseId = getEntrepriseId();
  const dataWithEntreprise = entrepriseId ? { ...data, entreprise_id: entrepriseId } : data;
  
  try {
    const response = await apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(dataWithEntreprise),
      ...options
    });
    const result = await response.json();
    
    // Les toasts sont maintenant gérés manuellement dans les composants avec toast.success/error
    // Ne plus afficher automatiquement pour éviter les doublons
    
    return result;
  } catch (error) {
    // L'erreur est déjà gérée dans apiCall avec le toast
    // Si silent est true, on ne veut pas afficher de toast supplémentaire
    if (!options.silent && !error.response) {
      handleApiError(error);
    }
    throw error;
  }
};

export const apiPatch = async (endpoint, data, options = {}) => {
  const entrepriseId = getEntrepriseId();
  const dataWithEntreprise = entrepriseId ? { ...data, entreprise_id: entrepriseId } : data;
  try {
    const response = await apiCall(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(dataWithEntreprise),
      ...options
    });
    return response.json();
  } catch (error) {
    if (!options.silent && !error.response) {
      handleApiError(error);
    }
    throw error;
  }
};

export const apiPut = async (endpoint, data, options = {}) => {
  try {
    // Ajouter l'entreprise_id aux données si pas déjà présent
    const entrepriseId = getEntrepriseId();
    const dataWithEntreprise = entrepriseId ? { ...data, entreprise_id: entrepriseId } : data;
    
    console.log('API PUT - Endpoint:', endpoint);
    console.log('API PUT - Data:', dataWithEntreprise);
    
    const response = await apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(dataWithEntreprise),
      ...options
    });
    
    console.log('API PUT - Response status:', response.status);
    console.log('API PUT - Response ok:', response.ok);
    
    const result = await response.json();
    console.log('API PUT - Result:', result);
    
    // Les toasts sont maintenant gérés manuellement dans les composants avec toast.success/error
    // Ne plus afficher automatiquement pour éviter les doublons
    
    return result;
  } catch (error) {
    console.error('Erreur dans apiPut:', error);
    if (!options.silent && !error.response) {
      handleApiError(error);
    }
    throw error;
  }
};

export const apiDelete = async (endpoint, options = {}) => {
  try {
    const response = await apiCall(endpoint, { method: 'DELETE', ...options });
    const result = await response.json();
    
    // Les toasts sont maintenant gérés manuellement dans les composants avec toast.success/error
    // Ne plus afficher automatiquement pour éviter les doublons
    
    return result;
  } catch (error) {
    if (!options.silent && !error.response) {
      handleApiError(error);
    }
    throw error;
  }
};

// Fonction pour faire un appel API DELETE avec body
export const apiDeleteWithBody = async (endpoint, data, options = {}) => {
  const token = getToken();
  
  if (!token) {
    throw new Error('Token d\'authentification manquant');
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include', // Inclure les cookies
    body: JSON.stringify(data)
  });
  
  // Si la réponse est 401 (non autorisé), rediriger vers login
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Session expirée');
  }

  if (!response.ok) {
    const error = {
      response: { status: response.status },
      status: response.status
    };
    if (!options.silent) {
      handleApiError(error, response.status);
    }
    throw error;
  }

  const result = await response.json();
  
  // Les toasts sont maintenant gérés manuellement dans les composants avec toast.success/error
  // Ne plus afficher automatiquement pour éviter les doublons
  
  return result;
};

// Fonction pour supprimer les cookies
const clearCookies = () => {
  console.log('Suppression des cookies côté client...');
  
  // Supprimer tous les cookies du domaine
  const cookies = document.cookie.split(";");
  console.log('Cookies actuels:', cookies);
  
  cookies.forEach((c) => {
    const eqPos = c.indexOf("=");
    const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
    if (name) {
      console.log('Suppression du cookie:', name);
      // Supprimer avec différents domaines et chemins (production)
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=app-secura.com`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.app-secura.com`;
    }
  });
  
};

// Fonction de déconnexion
export const logout = async () => {
  try {
    const token = getToken();
    if (token) {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include' // Inclure les cookies
      });
    }
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error);
  } finally {
    // Toujours supprimer les cookies côté client
    clearCookies();
  }
};
